import { RootState } from '../../../../../../init-store';
import { isDevelopmentEnvironmentSelector, isRoomAgentMode } from '../../../../../shared/features/config/selectors';
import { selectIsCommandListOpen } from '../../../../../shared/features/room-agent/dtmf/command-list/selectors';
import { selectIsDebugMenuOpen } from '../../../../../shared/features/room-agent/dtmf/debug-menu/selectors';
import { selectIsParticipantListOpen } from '../../../../../shared/features/room-agent/dtmf/participant-list/selectors';
import { getReactionIconSizes } from '../../../../../shared/helpers/get-reaction-icon-sizes';
import { Size } from '../../../../../shared/helpers/layout-manager/types';
import { getFormattedCallNumber, getParticipantsBySettings, selectMeetingName } from '../../../selectors';
import { drawAloneScreen } from './draw-alone-screen';
import { drawParticipantReaction } from './draw-participant-reaction';
import { drawParticipantStatuses } from './draw-participant-statuses';
import { drawDominant, drawPaginationArrows, drawParticipantInfo } from './drawer';
import { drawCanvasNotifications, selectActiveNotificationTypes } from './dtmf/canvas-notifications';
import { drawCommandList } from './dtmf/command-list';
import { drawDebugMenu } from './dtmf/debug-menu';
import { drawParticipantList } from './dtmf/participant-list';
import { ParticipantLayoutEntry } from './types';

/**
 * Рисует весь overlay (имена/иконки/бордеры/реакции + frame-level блоки) на указанном
 * контексте. Используется и при прямом рисовании в основной canvas (флаг=off),
 * и при отрисовке на offscreen canvas внутри OverlayManager (флаг=on).
 *
 * Логика 1:1 с тем, что сейчас делает drawConference в drawer.ts (блок после
 * video pass для каждого участника + frame-level блоки в конце функции).
 */
export function drawAllOverlay(
    ctx: CanvasRenderingContext2D,
    state: RootState,
    layout: ParticipantLayoutEntry[],
    size: Size
) {
    // Per-participant overlay.
    for (const entry of layout) {
        const {
            area,
            participant,
            refParticipant,
            params: { hasFrameBorder, statusIcons },
            isDrawDominant,
            shouldDrawParticipantStatuses,
            participantsCount,
            gridView,
            isMainSlot,
            isMobile,
            isPermissionsExtended,
            hasMicrophonePermission,
        } = entry;

        drawParticipantInfo(ctx, area, refParticipant ?? participant, {
            isPermissionsExtended,
            hasMicrophonePermission,
        });

        if (shouldDrawParticipantStatuses && statusIcons) {
            drawParticipantStatuses(ctx, area, statusIcons);
        }

        if (isDrawDominant && hasFrameBorder) {
            drawDominant(ctx, area);
        }

        const iconSizes = getReactionIconSizes(gridView, isMainSlot, isMobile, participantsCount);
        drawParticipantReaction(ctx, area, refParticipant ?? participant, iconSizes);
    }

    // Frame-level overlays.
    const visibleParticipants = getParticipantsBySettings(state).length;
    const shouldDrawAloneScreen =
        isRoomAgentMode(state) &&
        (!visibleParticipants || (isDevelopmentEnvironmentSelector(state) && visibleParticipants === 1));

    if (shouldDrawAloneScreen) {
        drawAloneScreen(ctx, { meetingName: selectMeetingName(state), callNumber: getFormattedCallNumber(state) });
    }

    const activeNotificationTypes = selectActiveNotificationTypes(state);
    drawCanvasNotifications(ctx, activeNotificationTypes, shouldDrawAloneScreen);

    if (selectIsCommandListOpen(state)) {
        drawCommandList(ctx, state);
    }

    if (selectIsParticipantListOpen(state)) {
        drawParticipantList(ctx, state);
    }

    if (selectIsDebugMenuOpen(state)) {
        drawDebugMenu(ctx, state);
    }

    if (isRoomAgentMode(state)) {
        drawPaginationArrows(ctx, state, size);
    }
}
